<template>
  <div
    v-if="!consentGiven"
    id="cookie-consent-banner"
    class="cookie-banner"
    role="dialog"
    aria-modal="false"
    aria-label="Aviso de cookies"
  >
    <div class="cookie-banner__row">
      <p class="cookie-banner__copy">
        Usamos cookies propias y de terceros para el funcionamiento del servicio, análisis de
        navegación y mejora de la experiencia. Las cookies no esenciales requieren tu consentimiento
        previo conforme a la Ley 25.326.
        <router-link to="/cookies" class="cookie-banner__link">Más info</router-link>
      </p>
      <div class="cookie-banner__actions">
        <button
          class="cookie-banner__btn cookie-banner__btn--primary"
          type="button"
          data-action="cookie-accept-all"
          @click="acceptAll"
        >
          Aceptar todas
        </button>
        <button
          class="cookie-banner__btn cookie-banner__btn--secondary"
          type="button"
          data-action="cookie-accept-necessary"
          @click="acceptNecessary"
        >
          Solo necesarias
        </button>
        <button
          class="cookie-banner__btn cookie-banner__btn--ghost"
          type="button"
          :aria-expanded="showConfig"
          aria-controls="cookie-config"
          data-action="cookie-configure"
          @click="toggleConfig"
        >
          Configurar
        </button>
      </div>
    </div>
    <div v-show="showConfig" id="cookie-config" class="cookie-banner__config">
      <div class="cookie-banner__config-row">
        <div>
          <strong class="cookie-banner__config-title">Cookies esenciales</strong><br />
          <span class="cookie-banner__config-desc"
            >Necesarias para el funcionamiento del sitio. No requieren consentimiento.</span
          >
        </div>
        <span class="cookie-banner__always">Siempre activas</span>
      </div>
      <div class="cookie-banner__config-row">
        <div>
          <strong class="cookie-banner__config-title">Cookies de analytics</strong><br />
          <span class="cookie-banner__config-desc"
            >Google Analytics y Microsoft Clarity. Permiten medir y analizar el uso del sitio.</span
          >
        </div>
        <label class="cookie-switch">
          <input
            v-model="analyticsChecked"
            type="checkbox"
            class="cookie-switch__input"
            data-input="cookie-analytics"
            aria-label="Cookies de analytics"
          />
          <span
            class="cookie-switch__track"
            :class="{ 'is-on': analyticsChecked }"
            aria-hidden="true"
          ></span>
        </label>
      </div>
      <button
        class="cookie-banner__btn cookie-banner__btn--primary"
        type="button"
        data-action="cookie-save-config"
        @click="saveConfig"
      >
        Guardar configuración
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';

const STORAGE_KEY = 'cookie-consent';
const consentGiven = ref(false);
const showConfig = ref(false);
const analyticsChecked = ref(false);

onMounted(() => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'all' || stored === 'necessary') {
    consentGiven.value = true;
    if (stored === 'all') {
      loadAnalytics();
    }
  }
  exposeApi();
});

function acceptAll(): void {
  localStorage.setItem(STORAGE_KEY, 'all');
  consentGiven.value = true;
  loadAnalytics();
}

function acceptNecessary(): void {
  localStorage.setItem(STORAGE_KEY, 'necessary');
  consentGiven.value = true;
}

function toggleConfig(): void {
  showConfig.value = !showConfig.value;
}

function saveConfig(): void {
  const val = analyticsChecked.value ? 'all' : 'necessary';
  localStorage.setItem(STORAGE_KEY, val);
  consentGiven.value = true;
  if (val === 'all') loadAnalytics();
}

let analyticsLoaded = false;
function loadAnalytics(): void {
  if (!analyticsLoaded) {
    analyticsLoaded = true;
    window.dispatchEvent(new CustomEvent('cookie-consent-analytics-accepted'));
  }
}

function getPreference(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

function isAnalyticsAllowed(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'all';
}

function hasConsented(): boolean {
  const val = localStorage.getItem(STORAGE_KEY);
  return val === 'all' || val === 'necessary';
}

function exposeApi(): void {
  (window as unknown as { CookieConsent: unknown }).CookieConsent = {
    isAnalyticsAllowed,
    hasConsented,
    getPreference,
  };
}
</script>

<style scoped>
/* Light consent banner: paper card, violet primary — same storage keys,
   same window.CookieConsent API, same analytics event as before. */
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: #fffcfd;
  border-top: 1px solid var(--line);
  box-shadow: 0 -12px 40px rgba(35, 27, 54, 0.12);
  padding: 16px 24px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 14px;
  color: var(--ink);
}
.cookie-banner__row {
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.cookie-banner__copy {
  flex: 1 1 300px;
  margin: 0;
  line-height: 1.5;
  color: var(--muted);
}
.cookie-banner__link {
  color: var(--violet);
  text-decoration: underline;
  font-weight: 600;
}
.cookie-banner__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.cookie-banner__btn {
  border-radius: 12px;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 13px;
  min-height: 44px;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}
.cookie-banner__btn--primary {
  background: var(--violet);
  color: #fff;
  border: 1px solid var(--violet);
}
.cookie-banner__btn--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(101, 65, 189, 0.35);
}
.cookie-banner__btn--secondary {
  background: #f3eff8;
  color: var(--ink);
  border: 1px solid var(--line);
}
.cookie-banner__btn--ghost {
  background: transparent;
  color: var(--muted);
  border: 1px solid var(--line);
  font-weight: 500;
}
.cookie-banner__config {
  max-width: 960px;
  margin: 12px auto 0;
  padding: 16px;
  background: var(--paper);
  border-radius: 12px;
  border: 1px solid var(--line);
}
.cookie-banner__config-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
}
.cookie-banner__config-title {
  font-size: 14px;
  color: var(--ink);
}
.cookie-banner__config-desc {
  font-size: 12px;
  color: var(--muted);
}
.cookie-banner__always {
  color: var(--muted);
  font-size: 12px;
  white-space: nowrap;
}
.cookie-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 26px;
  flex-shrink: 0;
}
.cookie-switch__input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}
.cookie-switch__track {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 22px;
  transition: 0.3s;
  background: #d1d5db;
}
.cookie-switch__track::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(35, 27, 54, 0.3);
  transition: transform 0.3s;
}
.cookie-switch__track.is-on {
  background: var(--violet);
}
.cookie-switch__track.is-on::after {
  transform: translateX(18px);
}
.cookie-switch__input:focus-visible + .cookie-switch__track {
  outline: 3px solid var(--violet);
  outline-offset: 3px;
}
button:focus-visible,
a:focus-visible,
input:focus-visible {
  outline: 3px solid var(--violet);
  outline-offset: 3px;
}
@media (prefers-reduced-motion: reduce) {
  .cookie-switch__track,
  .cookie-switch__track::after {
    transition: none;
  }
}
</style>
